import { IResult } from "@/types/map";
import distance from "@turf/distance";
import * as turf from "@turf/helpers";
import { loadModules } from "esri-loader";
import { throttle } from "ts-debounce-throttle";
import LaneQueueLength from "../../LaneQueueLength/arcgis/LaneQueueLength";
import { OverlayArcgis2D } from "../../Overlays/arcgis/OverlayArcgis2D";
import { OverlayArcgis3D } from "../../Overlays/arcgis/OverlayArcgis3D";
import SignalPhaseController from "../../SignalPhase/SignalPhaseController";
import { checkPoints } from "./CheckPoints";
import PanoramaView from "./PanoramaView";
import PassagewayView from "./PassagewayView";

export default class MapCenterCheck {
  private static intances: Map<string, MapCenterCheck>;
  private highlightTimers: Map<string, any> = new Map<string, any>();
  private stopFocusType: string[] = [];
  public static getInstance(view: __esri.SceneView | __esri.MapView) {
    const id = view.container.id;
    if (!MapCenterCheck.intances) {
      MapCenterCheck.intances = new Map();
    }
    let instance = MapCenterCheck.intances.get(id);
    if (!instance) {
      instance = new MapCenterCheck(view);
      MapCenterCheck.intances.set(id, instance);
    }
    return instance;
  }

  private view!: __esri.SceneView | __esri.MapView;
  private Point!: __esri.PointConstructor;

  private focusedInfo: any;
  private readonly focusDistance = 50;
  private watchFocusHandle!: __esri.WatchHandle;
  private watchCenterHandle!: __esri.WatchHandle;

  private panorama!: PanoramaView;
  private passageway!: PassagewayView;
  private signalPhaseController!: SignalPhaseController;
  private laneQueueLength!: LaneQueueLength;

  private panelInstances: Array<any> = [];

  public mapFocused!: (focusInfo: { type: string; id: string } | undefined) => void;
  public mapInteractive!: (viewPoint: __esri.Viewpoint) => void;

  private constructor(view: __esri.SceneView | __esri.MapView) {
    this.view = view;
    if (view.type == "3d") {
      this.panorama = PanoramaView.getInstance(view as __esri.SceneView);
      this.passageway = PassagewayView.getInstance(view as __esri.SceneView);
      this.signalPhaseController = SignalPhaseController.getInstance(view as __esri.SceneView);
      this.laneQueueLength = LaneQueueLength.getInstance(view as __esri.SceneView);
    }
  }

  /**
   * 检查地图中心点是否进入某个预设的焦点
   */
  public async startWatchMapFocus() {
    type MapModules = [typeof import("esri/geometry/Point")];
    const [Point] = await (loadModules(["esri/geometry/Point"]) as Promise<MapModules>);
    this.Point = Point;
    if (this.view.type == "3d") {
      await this.panorama.initialize();
      await this.passageway.initialize();
    }

    this.watchFocusHandle = this.view.watch(
      "interacting,animation",
      throttle(() => {
        this.checkFocusPoint();
      }, 500)
    );

    this.checkFocusPoint();
  }

  public async toggleWatchMapFocus(params: { types: string[] }) {
    params.types.forEach((type, index) => {
      if (this.stopFocusType.indexOf(type) > -1) {
        this.stopFocusType.splice(index, 1);
      } else {
        this.stopFocusType.push(type);
      }
    });
    if (params.types.indexOf("fireball") > -1) {
      this.panorama.toggleFxLayer();
    }
  }

  public stopWatchMapFocus() {
    if (this.watchFocusHandle) {
      this.watchFocusHandle.remove();
    }
    this.panorama.hidePanorama();
    this.passageway.hidePassageways();
  }

  private checkFocusPoint() {
    // 移动路口信息面板
    // if (this.panelInstances.length > 0) {
    //   for (let instance of this.panelInstances) {
    //     const coordinate = instance.$props.coordinate;
    //     const screenPoint = this.view.toScreen(
    //       new this.Point({
    //         x: coordinate[0],
    //         y: coordinate[1],
    //       })
    //     );
    //     instance.$props.center = [screenPoint.x, screenPoint.y];
    //   }
    // }

    const center = this.view.center;
    const scale = this.view.scale;
    const result = checkPoints.filter(pointInfo => {
      const distanceBetweenMapCenter = distance(turf.point([center.longitude, center.latitude]), turf.point(pointInfo.position), { units: "meters" });
      return (
        distanceBetweenMapCenter < (pointInfo.focusDistance || this.focusDistance) &&
        ((pointInfo.maxScale === 0 && scale < pointInfo.minScale) || (pointInfo.minScale === 0 && scale > pointInfo.maxScale) || (pointInfo.minScale !== 0 && pointInfo.maxScale !== 0 && pointInfo.maxScale < scale && scale < pointInfo.minScale))
      );
    });

    if (this.focusedInfo && (result.length === 0 || result[0].id !== this.focusedInfo.id)) {
      // 从原有焦点中脱离
      switch (this.focusedInfo.type) {
        case "panorama":
          this.panorama.hidePanorama();
          break;

        case "passageway":
          this.passageway.hidePassageways();
          break;

        case "cross":
          this.signalPhaseController.stopSignalPhase(this.focusedInfo.id);
          this.laneQueueLength.hideQueueLength(this.focusedInfo.id);
          break;

        default:
          break;
      }
      this.focusedInfo = undefined;
      this.mapFocused(undefined);
    }
    if (result.length !== 0 && this.focusedInfo?.id !== result[0].id) {
      // 进入焦点
      this.focusedInfo = result[0];
      const { type, id } = this.focusedInfo;

      switch (type) {
        case "panorama":
          if (this.stopFocusType.indexOf("panorama") < 0) {
            this.panorama.showPanorama(id);
          }
          break;

        case "passageway":
          if (this.stopFocusType.indexOf("passageway") < 0) {
            this.passageway.showPassageways();
          }
          break;

        case "cross":
          if (this.stopFocusType.indexOf("cross") < 0) {
            this.laneQueueLength.showQueueLength(id);
          }
          break;
      }
      this.mapFocused({ type, id });
    }
  }

  public async setMapFocus(focusInfo: { type: string; id: string; scaleFactor?: number; highlightOpt?: any }): Promise<IResult> {
    const scaleFactor = focusInfo.scaleFactor || 1;

    // 通道动态生成，坐标不在配置文件中
    if (focusInfo.type === "passageway") {
      return this.passageway.setPassagewayCenter(focusInfo.id, scaleFactor);
    } else {
      const focus = checkPoints.find(point => point.type === focusInfo.type && point.id === focusInfo.id);

      if (focus) {
        let center = focusInfo.highlightOpt ? focusInfo.highlightOpt.showCenter === true : true;
        if (center) {
          if (focus.minScale === 0 && focus.maxScale === 0) {
            await this.view.goTo({ center: focus.position });
          } else if (focus.minScale === 0) {
            await this.view.goTo({
              center: focus.position,
              scale: focus.maxScale * scaleFactor,
            });
          } else {
            await this.view.goTo({
              center: focus.position,
              scale: focus.minScale * scaleFactor,
            });
          }
        }
        if (focusInfo.highlightOpt) {
          let url = focusInfo.highlightOpt.url || "assets/image/highlight.png";
          let width = focusInfo.highlightOpt.width ? focusInfo.highlightOpt.width.toString() + "px" : "40px";
          let height = focusInfo.highlightOpt.height ? focusInfo.highlightOpt.height.toString() + "px" : "40px";
          let anchor = focusInfo.highlightOpt.anchor || "center";
          let offset = focusInfo.highlightOpt.offset || 0;
          let type = this.getType(url);
          let time = focusInfo.highlightOpt.time === undefined ? 5 : focusInfo.highlightOpt.time;
          let hightOverlay = {
            defaultSymbol: {
              type: "point",
            },
            type: type,
            custom: {
              content: "<img src='" + url + "' width='" + width + "' height='" + height + "'></img>",
              anchor: anchor,
              yoffset: offset,
            },
            overlays: [
              {
                id: type + (Math.random() * 10000).toFixed(0),
                geometry: { x: focus.position[0], y: focus.position[1] },
              },
            ],
          };
          let overlay: any;
          if (this.view.type == "3d") {
            overlay = OverlayArcgis3D.getInstance(this.view as __esri.SceneView);
          } else {
            overlay = OverlayArcgis2D.getInstance(this.view as __esri.MapView);
          }
          if (this.highlightTimers.get(type)) {
            //清除计时器
            clearTimeout(this.highlightTimers.get(type));
            this.highlightTimers.delete(type);
          }
          //删除高亮
          overlay.deleteOverlays({ types: [type] });
          if (time > 0) {
            let timeHander = setTimeout(() => {
              overlay.deleteOverlays({ types: [type] });
            }, time * 1000);
            this.highlightTimers.set(type, timeHander);
          }
          //高亮
          overlay.addOverlays(hightOverlay);
          return { status: 0, message: "" };
        }
        // 如果当前在监视地图，显示
        // 停止地图监视以后不再显示，只提供定位
        if (this.watchFocusHandle) {
          this.checkFocusPoint();
        }
        return { status: 0, message: "" };
      } else {
        return { status: -1, message: "no point" };
      }
    }
  }

  private getType(url: string) {
    let type: any = url.split("/").pop()?.split(".")[0];
    return type;
  }

  public startWatchMapCenter() {
    this.watchCenterHandle = this.view.watch("interacting", () => {
      if (!this.view.interacting) {
        // console.log(this.view.viewpoint.toJSON());
        this.mapInteractive(this.view.viewpoint);
      }
    });
  }

  public stopWatchMapCenter() {
    if (this.watchCenterHandle) {
      this.watchCenterHandle.remove();
    }
  }
}
